import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type DashboardSearchType, useOrderSearch } from '@/lib/api/orders/search'
import { cn, getOrderStatusBadgeClasses } from '@/lib/utils'
import { format } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const DEFAULT_VISIBLE_ROWS = 3

const COLUMNS = ['Order Number', 'Email Address', 'Status', 'Items', 'Order Date']

// `wc-awaiting-decision` → `Awaiting Decision`
const formatOrderStatus = (status: string) =>
  status
    .replace(/^wc-/, '')
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// PHP returns `YYYY-MM-DD HH:mm:ss`, which Safari won't parse without the `T`.
const formatOrderDate = (createdAt: string) => {
  const date = new Date(createdAt.replace(' ', 'T'))
  return Number.isNaN(date.getTime()) ? '-' : format(date, 'dd MMM yyyy')
}

export default function OrderSearchResults({
  searchQuery,
  searchType
}: {
  searchQuery: string
  searchType: DashboardSearchType
}) {
  const navigate = useNavigate()
  // Tracks which query was expanded, so a new search starts collapsed again.
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null)
  const showAll = expandedQuery === searchQuery
  const { data: rows = [], isLoading, isFetching, isError } = useOrderSearch(searchQuery, searchType)

  if (isLoading) {
    return <Loader2 className='mx-auto my-8 h-7 w-7 animate-spin text-custom-neutral-500' />
  }

  if (isError) {
    return <p className='text-sm text-custom-neutral-500'>Something went wrong while searching. Please try again.</p>
  }

  const visibleRows = showAll ? rows : rows.slice(0, DEFAULT_VISIBLE_ROWS)

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <h3 className='text-lg font-semibold text-secondary'>Medication Requests</h3>
          <span className='min-w-6 rounded-md bg-custom-gray-3 px-2 py-0.5 text-center text-sm font-semibold text-custom-neutral-800'>
            {rows.length}
          </span>
        </div>

        {rows.length > DEFAULT_VISIBLE_ROWS && (
          <button
            type='button'
            className='cursor-pointer text-sm font-medium text-primary underline-offset-4 hover:underline'
            onClick={() => setExpandedQuery(showAll ? null : searchQuery)}
          >
            {showAll ? 'Show less' : 'View all'}
          </button>
        )}
      </div>

      <div
        className={cn(
          'rounded-md border transition-opacity duration-150',
          isFetching && 'pointer-events-none opacity-50'
        )}
      >
        <Table>
          <TableHeader>
            <TableRow className='bg-custom-gray-3'>
              {COLUMNS.map((column, i) => (
                <TableHead
                  key={column}
                  className={cn(
                    'h-14 text-sm font-medium leading-6 text-custom-neutral-900',
                    i === 0 ? 'pl-6' : 'pl-4'
                  )}
                >
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.length ? (
              visibleRows.map(row => {
                const status = formatOrderStatus(row.status)

                return (
                  <TableRow
                    key={row.medRequestId}
                    className='cursor-pointer'
                    onClick={() => navigate(`/request/${row.medRequestId}`)}
                  >
                    <TableCell className='p-4 pl-6 text-sm text-custom-neutral-800'>{row.orderNumber ?? '-'}</TableCell>
                    <TableCell className='p-4 text-sm text-custom-neutral-800'>{row.email || '-'}</TableCell>
                    <TableCell className='p-4'>
                      <span
                        className={cn(
                          'grid w-max place-content-center rounded-2xl border px-4 py-2 text-[13px] leading-5',
                          getOrderStatusBadgeClasses(status)
                        )}
                      >
                        {status}
                      </span>
                    </TableCell>
                    <TableCell className='p-4 text-sm text-custom-neutral-800'>{row.itemName ?? '-'}</TableCell>
                    <TableCell className='p-4 text-sm text-custom-neutral-800'>
                      {formatOrderDate(row.createdAt)}
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} className='h-24 text-center text-sm text-custom-neutral-500'>
                  No medication requests found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
